import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronDown, Copy, FolderOpen, Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useGuardedNavigate, useLeaveGuard } from "@/hooks/useGuardedNavigate";
import { duplicateDeck } from "@/lib/deckBundle";
import { listDecks, type DeckIndexEntry } from "@/lib/idb";
import { cn } from "@/lib/utils";
import { createNewDeck } from "@/store/bootstrap";
import { setTitle } from "@/store/documentSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

// How many recent decks the menu offers before deferring to the documents home.
const RECENT_LIMIT = 8;

// The TopBar's deck control (TODO §12.4.2): the title field, plus a dropdown of recent
// decks with "All documents…" and "New document".
//
// Picking a deck **navigates** — it never swaps state in place. That is the §12.1 rule
// showing through: the open deck is a property of the URL, so switching decks is a route
// change, and the editor subtree remounts under its `key={deckId}` rather than being
// unwound field by field (§12.2.4).
export function DeckSwitcher({ readOnly = false }: { readOnly?: boolean }) {
  const dispatch = useAppDispatch();
  const title = useAppSelector((s) => s.document.title);
  const currentDeckId = useAppSelector((s) => s.document.id);
  const navigate = useNavigate();
  const go = useGuardedNavigate();
  const mayLeave = useLeaveGuard();

  const [open, setOpen] = useState(false);
  const [decks, setDecks] = useState<DeckIndexEntry[]>([]);

  // Read the list when the menu opens rather than on mount: the editor should not pay for a
  // storage round-trip it may never show, and opening the menu is exactly the moment the
  // list needs to be fresh.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void listDecks().then((rows) => {
      if (!cancelled) setDecks(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Ask about an unsaved style draft BEFORE minting the deck — creating it first and then
  // being cancelled would leave an empty document behind on the documents home.
  const newDocument = async () => {
    if (!mayLeave()) return;
    navigate(`/d/${await createNewDeck()}`);
  };

  // §12.4.3 Duplicate-of-current, the third new-document entry point. It reads the deck
  // from storage rather than from Redux so the copy includes the grid bodies and image
  // blobs, which live in their own stores and never enter the document slice.
  const duplicateCurrent = async () => {
    if (!mayLeave()) return;
    const copyId = await duplicateDeck(currentDeckId);
    if (!copyId) {
      toast.error("This deck isn’t in storage yet");
      return;
    }
    navigate(`/d/${copyId}`);
  };

  const recent = decks.slice(0, RECENT_LIMIT);

  return (
    <div className="flex min-w-0 items-center gap-1">
      <Input
        value={title}
        onChange={(e) => dispatch(setTitle(e.target.value))}
        placeholder="Untitled deck"
        aria-label="Deck title"
        readOnly={readOnly}
        className={cn(
          "h-8 w-64 border-0 bg-transparent text-center text-sm font-medium text-white placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-slate-600",
          readOnly && "cursor-default",
        )}
      />
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-white hover:bg-white/10 hover:text-white"
            aria-label="Switch document"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-72">
          <DropdownMenuLabel>Recent documents</DropdownMenuLabel>
          {recent.map((entry) => (
            <DropdownMenuItem
              key={entry.deckId}
              onClick={() => go(`/d/${entry.deckId}`)}
            >
              <Check
                className={cn(
                  "h-4 w-4 shrink-0",
                  entry.deckId === currentDeckId ? "opacity-100" : "opacity-0",
                )}
              />
              <span className="min-w-0 flex-1 truncate">
                {entry.title || "Untitled deck"}
              </span>
            </DropdownMenuItem>
          ))}
          {recent.length === 0 && (
            <DropdownMenuItem disabled>No other documents</DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => go("/documents")}>
            <FolderOpen className="h-4 w-4" /> All documents…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => void newDocument()}>
            <Plus className="h-4 w-4" /> New document
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => void duplicateCurrent()}>
            <Copy className="h-4 w-4" /> Duplicate this document
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
